class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this._luxSummaryClick = this._onLuxSummaryClick.bind(this);
    this._luxSummaryPointerDown = this._onLuxSummaryPointerDown.bind(this);
    this._luxPointerEnter = this._onLuxPointerEnter.bind(this);
    this._luxPointerLeave = this._onLuxPointerLeave.bind(this);
    this._luxDocumentPointerMove = this._onLuxDocumentPointerMove.bind(this);
    this._luxLeaveTimer = null;
    this._luxDocumentTracking = false;
    this._luxPointerPosition = null;
    this._luxuryClickBound = false;
    this._luxSuppressNextClick = false;
    this._luxSwitchTimer = null;
    this._luxSwitchCleanupTimers = [];
  }

  connectedCallback() {
    /* Defer past layout: upgrade order / theme editor can delay ancestor <header>. */
    requestAnimationFrame(() => {
      this.header = this.closest('.header-wrapper') || document.querySelector('.header-wrapper');
      this._bindLuxuryClickNav();
    });
  }

  disconnectedCallback() {
    this._unbindLuxuryClickNav();
  }

  _bindLuxuryClickNav() {
    if (this._luxuryClickBound) return;
    if (!this.closest('.header--luxury')) return;
    this._luxSummaryEl = this.mainDetailsToggle?.querySelector('summary');
    if (this._luxSummaryEl) {
      this._luxSummaryEl.addEventListener('pointerdown', this._luxSummaryPointerDown, true);
      this._luxSummaryEl.addEventListener('click', this._luxSummaryClick, true);
      this._luxSummaryEl.addEventListener('mouseenter', this._luxPointerEnter);
      this._luxSummaryEl.addEventListener('mouseleave', this._luxPointerLeave);
    }
    if (this.content) {
      this.content.addEventListener('mouseenter', this._luxPointerEnter);
      this.content.addEventListener('mouseleave', this._luxPointerLeave);
    }
    this._luxuryClickBound = true;
  }

  _unbindLuxuryClickNav() {
    if (!this._luxuryClickBound) return;
    if (this._luxSummaryEl) {
      this._luxSummaryEl.removeEventListener('pointerdown', this._luxSummaryPointerDown, true);
      this._luxSummaryEl.removeEventListener('click', this._luxSummaryClick, true);
      this._luxSummaryEl.removeEventListener('mouseenter', this._luxPointerEnter);
      this._luxSummaryEl.removeEventListener('mouseleave', this._luxPointerLeave);
      this._luxSummaryEl = null;
    }
    if (this.content) {
      this.content.removeEventListener('mouseenter', this._luxPointerEnter);
      this.content.removeEventListener('mouseleave', this._luxPointerLeave);
    }
    this._clearLuxLeaveTimer();
    this._clearLuxSwitchTimers();
    this._luxuryClickBound = false;
    this._removeLuxDocumentTracking();
  }

  _onLuxSummaryPointerDown(event) {
    if (window.matchMedia('(min-width: 990px)').matches && this.closest('.header--luxury')) {
      this.header = this.closest('.header-wrapper') || this.header;
      if (!this.mainDetailsToggle.hasAttribute('open')) {
        this.header?.classList.add('header-wrapper--nav-disclosure-open');
        this._syncLuxuryHeaderSurface(true);
      }
    }

    if (!this._shouldSwitchLuxuryMega()) return;
    this._luxSuppressNextClick = true;
    this._syncLuxuryMegaDirectoryOffset();
    this._switchLuxuryMega(event);
  }

  _onLuxSummaryClick(event) {
    if (window.matchMedia('(max-width: 989px)').matches) return;
    if (!this.closest('.header--luxury')) return;

    if (this._luxSuppressNextClick) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this._luxSuppressNextClick = false;
      return;
    }

    this._syncLuxuryMegaDirectoryOffset();

    if (this._shouldSwitchLuxuryMega()) {
      this._switchLuxuryMega(event);
      return;
    }

    const willOpen = !this.mainDetailsToggle.hasAttribute('open');
    const menu = this.closest('.header__inline-menu');
    const openDetails = menu ? Array.from(menu.querySelectorAll('header-menu > details[open]')) : [];

    if (willOpen && menu) {
      openDetails.forEach((det) => {
        if (det !== this.mainDetailsToggle) det.removeAttribute('open');
      });
    }
    this._refreshLuxuryHeaderBottomVar();
    this._clearLuxLeaveTimer();
  }

  _shouldSwitchLuxuryMega() {
    if (window.matchMedia('(max-width: 989px)').matches) return false;
    if (!this.closest('.header--luxury')) return false;
    if (this.mainDetailsToggle.hasAttribute('open')) return false;

    const menu = this.closest('.header__inline-menu');
    const openDetails = menu ? Array.from(menu.querySelectorAll('header-menu > details[open]')) : [];
    return openDetails.some((det) => det !== this.mainDetailsToggle);
  }

  _switchLuxuryMega(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const menu = this.closest('.header__inline-menu');
    const openDetails = menu ? Array.from(menu.querySelectorAll('header-menu > details[open]')) : [];

    this._clearAllLuxurySwitchTimers(menu);
    this.header?.classList.add('header-wrapper--nav-disclosure-open', 'header-wrapper--mega-switching');
    this._syncLuxuryHeaderSurface(true);
    this.mainDetailsToggle.classList.add('aelein-mini-mega--instant-switch');
    this.mainDetailsToggle.setAttribute('open', '');
    this.mainDetailsToggle.querySelector('summary')?.setAttribute('aria-expanded', true);
    this._syncLuxuryMegaDirectoryOffset();
    this._refreshLuxuryHeaderBottomVar();
    this._clearLuxLeaveTimer();
    this.syncLuxuryNavPanel();

    const closePrevious = () => {
      openDetails.forEach((det) => {
        if (det === this.mainDetailsToggle) return;
        det.removeAttribute('open');
        det.querySelector('summary')?.setAttribute('aria-expanded', false);
        det.classList.remove('aelein-mini-mega--switch-previous');
      });
    };
    openDetails.forEach((det) => {
      if (det !== this.mainDetailsToggle) det.classList.add('aelein-mini-mega--switch-previous');
    });
    const cleanupTimer = window.setTimeout(closePrevious, 220);
    this._luxSwitchCleanupTimers.push(cleanupTimer);

    if (this._luxSwitchTimer) window.clearTimeout(this._luxSwitchTimer);
    this._luxSwitchTimer = window.setTimeout(() => {
      this.header?.classList.remove('header-wrapper--mega-switching');
      this.mainDetailsToggle.classList.remove('aelein-mini-mega--instant-switch');
      this._luxSwitchTimer = null;
    }, 360);
  }

  _clearLuxLeaveTimer() {
    if (this._luxLeaveTimer) {
      clearTimeout(this._luxLeaveTimer);
      this._luxLeaveTimer = null;
    }
  }

  _clearLuxSwitchTimers() {
    if (!this._luxSwitchCleanupTimers.length) return;
    this._luxSwitchCleanupTimers.forEach((timer) => window.clearTimeout(timer));
    this._luxSwitchCleanupTimers = [];
  }

  _clearAllLuxurySwitchTimers(menu) {
    if (!menu) return;
    menu.querySelectorAll('header-menu').forEach((headerMenu) => {
      if (typeof headerMenu._clearLuxSwitchTimers === 'function') headerMenu._clearLuxSwitchTimers();
      if (headerMenu._luxSwitchTimer) {
        window.clearTimeout(headerMenu._luxSwitchTimer);
        headerMenu._luxSwitchTimer = null;
      }
    });
  }

  _scheduleLuxAutoClose() {
    if (this._luxLeaveTimer) return;
    this._luxLeaveTimer = window.setTimeout(() => {
      this._luxLeaveTimer = null;
      if (this._luxPointerPosition && this._isPointInsideLuxuryDisclosure(this._luxPointerPosition)) return;
      this.close();
      this.syncLuxuryNavPanel();
    }, 850);
  }

  _addLuxDocumentTracking() {
    if (this._luxDocumentTracking) return;
    document.addEventListener('pointermove', this._luxDocumentPointerMove);
    document.addEventListener('mousemove', this._luxDocumentPointerMove);
    this._luxDocumentTracking = true;
  }

  _removeLuxDocumentTracking() {
    if (!this._luxDocumentTracking) return;
    document.removeEventListener('pointermove', this._luxDocumentPointerMove);
    document.removeEventListener('mousemove', this._luxDocumentPointerMove);
    this._luxDocumentTracking = false;
  }

  _isPointInsideLuxuryDisclosure(event) {
    const x = event.clientX;
    const y = event.clientY;

    if (typeof x !== 'number' || typeof y !== 'number') return false;

    return [this._luxSummaryEl, this.content].some((element) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    });
  }

  _onLuxPointerEnter(event) {
    if (window.matchMedia('(max-width: 989px)').matches) return;
    if (!this.mainDetailsToggle.hasAttribute('open')) return;
    this._luxPointerPosition = { clientX: event.clientX, clientY: event.clientY };
    this._clearLuxLeaveTimer();
  }

  _onLuxPointerLeave(event) {
    if (window.matchMedia('(max-width: 989px)').matches) return;
    if (!this.mainDetailsToggle.hasAttribute('open')) return;

    const next = event.relatedTarget;
    if (next && (this._luxSummaryEl?.contains(next) || this.content?.contains(next))) return;

    this._scheduleLuxAutoClose();
  }

  _onLuxDocumentPointerMove(event) {
    if (window.matchMedia('(max-width: 989px)').matches) return;
    if (!this.mainDetailsToggle.hasAttribute('open')) return;

    this._luxPointerPosition = { clientX: event.clientX, clientY: event.clientY };

    if (this._isPointInsideLuxuryDisclosure(event)) {
      this._clearLuxLeaveTimer();
      return;
    }

    this._scheduleLuxAutoClose();
  }

  onToggle() {
    super.onToggle();

    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    /* Dawn: mega max-height uses this var once; must NOT return early or luxury panel class never re-syncs. */
    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') === '') {
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
      );
    }

    if (this.header.classList.contains('header-wrapper--luxury-nav-reveal') && this.mainDetailsToggle.open) {
      this._syncLuxuryMegaDirectoryOffset();
      this._refreshLuxuryHeaderBottomVar();
    }

    if (this.header.classList.contains('header-wrapper--luxury-nav-reveal')) {
      if (this.mainDetailsToggle.open) {
        this._addLuxDocumentTracking();
      } else {
        this._removeLuxDocumentTracking();
        this._clearLuxLeaveTimer();
      }
    }

    this.syncLuxuryNavPanel();
  }

  _bindLuxuryScrimClose() {
    const section = this.header?.closest('.section-header');
    const scrim = section?.querySelector('.aelein-mega-page-scrim');
    if (!scrim || scrim.dataset.aeleinMegaScrimBound === 'true') return;

    scrim.dataset.aeleinMegaScrimBound = 'true';
    scrim.addEventListener('click', () => {
      section.querySelectorAll('.header__inline-menu--aelein-mini-mega details[open]').forEach((details) => {
        details.removeAttribute('open');
        details.querySelector('summary')?.setAttribute('aria-expanded', false);
      });
      this.syncLuxuryNavPanel();
    });
  }

  /** Keeps fixed mega / dropdown `top` aligned with the real header bottom (hit area = visible panel). */
  _refreshLuxuryHeaderBottomVar() {
    if (!this.header?.classList.contains('header-wrapper--luxury-nav-reveal')) return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  _syncLuxuryMegaDirectoryOffset() {
    if (window.matchMedia('(max-width: 989px)').matches) return;
    if (!this._luxSummaryEl) return;

    const summaryRect = this._luxSummaryEl.getBoundingClientRect();
    const summaryStyle = window.getComputedStyle(this._luxSummaryEl);
    const summaryPaddingLeft = parseFloat(summaryStyle.paddingLeft) || 0;
    const left = Math.round(summaryRect.left + summaryPaddingLeft + 13);
    if (!Number.isFinite(left)) return;

    this.mainDetailsToggle.style.setProperty('--aelein-mega-guide-left', `${Math.max(0, left - 20)}px`);
    this.mainDetailsToggle.style.setProperty('--aelein-mega-directory-left', `${left}px`);
  }

  syncLuxuryNavPanel() {
    if (!this.header) return;
    if (!this.header.classList.contains('header-wrapper--luxury-nav-reveal')) {
      this.header.classList.remove('header-wrapper--nav-disclosure-open');
      this._syncLuxuryHeaderSurface(false);
      return;
    }
    const anyOpen =
      this.header.querySelector('.header__inline-menu > ul.list-menu--inline > li > header-menu > details[open]') ||
      this.header.querySelector('details.mega-menu--unified[open]');
    const isOpen = Boolean(anyOpen);
    this.header.classList.toggle('header-wrapper--nav-disclosure-open', isOpen);
    this._syncLuxuryHeaderSurface(isOpen);
    this._bindLuxuryScrimClose();
  }

  _syncLuxuryHeaderSurface(isOpen) {
    const headerRow = this.header?.querySelector('.header--luxury');
    const targets = [this.header, headerRow].filter(Boolean);
    const colorTargets = headerRow
      ? headerRow.querySelectorAll(
          '.header__menu-item, .header__menu-item span, .header__active-menu-item, .header__utility-link, .header__utility-link span, .disclosure__button, .disclosure__button span, .header__icon, .header__icon .svg-wrapper, .icon-caret'
        )
      : [];
    const logoTargets = headerRow ? headerRow.querySelectorAll('.header__heading-logo') : [];

    targets.forEach((element) => {
      if (isOpen) {
        element.style.setProperty('background', '#f6f6f4', 'important');
        element.style.setProperty('background-color', '#f6f6f4', 'important');
        element.style.setProperty('background-image', 'none', 'important');
      } else {
        element.style.removeProperty('background');
        element.style.removeProperty('background-color');
        element.style.removeProperty('background-image');
      }
    });

    colorTargets.forEach((element) => {
      if (isOpen) {
        element.style.setProperty('color', 'rgba(18, 18, 18, 0.92)', 'important');
      } else {
        element.style.removeProperty('color');
      }
    });

    logoTargets.forEach((element) => {
      if (isOpen) {
        element.style.setProperty('filter', 'brightness(0)', 'important');
        element.style.setProperty('opacity', '1', 'important');
        element.style.setProperty('visibility', 'visible', 'important');
      } else {
        element.style.removeProperty('filter');
        element.style.removeProperty('opacity');
        element.style.removeProperty('visibility');
      }
    });
  }
}

customElements.define('header-menu', HeaderMenu);
